/*************************************************************************
* ADOBE CONFIDENTIAL
* ___________________
*
* Copyright 2019 Adobe
* All Rights Reserved.
*
* NOTICE: All information contained herein is, and remains
* the property of Adobe and its suppliers, if any. The intellectual
* and technical concepts contained herein are proprietary to Adobe
* and its suppliers and are protected by all applicable intellectual
* property laws, including trade secret and copyright laws.
* Dissemination of this information or reproduction of this material
* is strictly forbidden unless prior written permission is obtained
* from Adobe.
**************************************************************************/
// We have global UAParser defined by dc-sdk-launcher
/* global UAParser */

import ServiceRegistry from "bundle/util/ServiceRegistry";
import IFrameMessageTypes from "common/constants/IFrameMessageTypes";
import { UIActionPositionTypes, UIActionIconTypes } from "bundle/constants/UIActionConstants";
import { getCurrentEnv } from "bundle/util/EnvUtil";
import AppConstants from "app/constants/AppConstants";

const stateKey = "statePreSignIn";

function logInConsole(title, message, type) {
    if (getCurrentEnv(window.location.host) !== "prod") {
        if (type === AppConstants.LOG_TYPE.ERROR) {
            // eslint-disable-next-line no-console
            console.error(title, message || "");
        } else {
            // eslint-disable-next-line no-console
            console.info(title, message || "");
        }
    }
}

function saveState(state) {
    try {
        sessionStorage.setItem(stateKey, JSON.stringify(state));
        return true;
    } catch (e) {
        return false;
    }
}

function getState() {
    try {
        const previewConfig = ServiceRegistry.getService("AppStore").configStore.getActionConfig("preview");
        const stateStr = sessionStorage.getItem(stateKey);
        let state = stateStr ? JSON.parse(stateStr) : undefined;
        if (previewConfig.executeDropinId) {
            if (state === undefined) {
                state = {};
            }
            state.dropinName = previewConfig.executeDropinId;
        }
        return state;
    } catch (e) {
        return undefined;
    }
}

function resetState() {
    try {
        return sessionStorage.removeItem(stateKey);
    } catch (e) {
        return false;
    }
}

function getMissingCallbacks(callbackList) {
    const missingCallbacks = [];
    const callbackService = ServiceRegistry.getService("CallbackService");
    callbackList.forEach(callbackType => {
        if (!callbackService.hasCallback(callbackType)) {
            missingCallbacks.push(callbackType);
        }
    });
    return missingCallbacks;
}

const isIE = () => window.navigator.userAgent.indexOf("MSIE") > -1
    || window.navigator.userAgent.indexOf("Trident") > -1;

const isLegacyEdge = () => {
    const ua = new UAParser();
    const browserInfo = ua.getBrowser();
    return browserInfo && browserInfo.name === "Edge" && parseInt(browserInfo.major, 10) < 50;
};

const isIOSDevice = () => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    return /iPhone|iPad|iPod/i.test(userAgent); // iPhone, iPad, or iPod
};

function getIOSVersion() {
    if (isIOSDevice()) {
        const userAgent = navigator.userAgent;
        const iOSVersionRegex = /OS (\d+_\d+(_\d+)?)/;
        const versionMatch = userAgent.match(iOSVersionRegex);

        if (versionMatch) {
            // Replace underscores with dots to make it a standard version format
            return versionMatch[1].replace(/_/g, ".");
        }
    }
    return null;
}

const isIOSVersion18OrAbove = iOSVersion => {
    if (!iOSVersion) return false;
    // Split the version string into its components (major, minor, patch)
    const [major] = iOSVersion.split(".").map(Number);
    return major >= 18;
};

const isSafariBrowser = () => {
    const userAgent = navigator.userAgent;
    // Safari user agent typically contains 'Safari' and 'Version' but does not include 'Chrome' or 'Android'
    return /^((?!chrome|android).)*safari/i.test(userAgent);
};

function getSafariVersion() {
    if (isSafariBrowser()) {
        const userAgent = navigator.userAgent;
        const safariVersionRegex = /Version\/(\d+(\.\d+)?)/; // Extracts the Safari version
        const versionMatch = userAgent.match(safariVersionRegex);

        if (versionMatch) {
            return versionMatch[1]; // Safari version (e.g., "18.0")
        }
    }
    return null;
}

const isSafariVersion18OrAbove = safariVersion => {
    if (!safariVersion) return false;
    // Split the version string into its components (major, minor, patch)
    const [major] = safariVersion.split(".").map(Number);
    return major >= 18;
};

const dispatchEvent = evt => {
    const event = document.createEvent("Event");
    event.initEvent(evt, true, true);
    document.dispatchEvent(event);
};

const dispatchUIActionClickEvent = uiAction => {
    // check to handle report abuse open from full screen mode
    if (uiAction.position === UIActionPositionTypes.TOPBAR_ELLIPSES
        && uiAction.iconType === UIActionIconTypes.REPORT_ABUSE
        && ServiceRegistry.getService("AppStore").inFullScreen) {
        ServiceRegistry.getService("AppStore").setInFullScreen(false);
    }
    ServiceRegistry.getService("MessagingService")
        .sendMessage(IFrameMessageTypes.CUSTOM_ICON_CLICK, { id: uiAction.id });
};

const getEmbedApiVersion = () => {
    let version = ServiceRegistry.getService("AppStore").configStore.getConfig().viewSDKAppVersion;
    if (version) {
        version = version.split("_")[1];
    }
    return version;
};

export {
    saveState,
    resetState,
    getState,
    getMissingCallbacks,
    isIE,
    isLegacyEdge,
    dispatchEvent,
    dispatchUIActionClickEvent,
    getEmbedApiVersion,
    isIOSDevice,
    getIOSVersion,
    isIOSVersion18OrAbove,
    isSafariBrowser,
    getSafariVersion,
    isSafariVersion18OrAbove,
    logInConsole,
};
